import React, { useContext, createContext, useState, useCallback } from 'react';
import PropTypes from 'prop-types';

/**
 * Error Context for global error state management
 * Used for API errors and network failures that should be handled gracefully
 */
const ErrorContext = createContext();

/**
 * Custom hook to access error context
 * @return {Object} { sosError, handleError, clearError, logError }
 */
export const useError = () => {
  const context = useContext(ErrorContext);
  if (!context) {
    throw new Error('useError must be used within ErrorProvider');
  }
  return context;
};

/**
 * ErrorProvider - Manages global error state
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Child components
 * @return {JSX.Element} Error context provider
 */
export const ErrorProvider = ({ children }) => {
  const [sosError, setError] = useState(null);

  /**
   * Handle error - sets error in global state
   * Only use for critical errors (network failures, auth errors)
   * For UI errors, handle locally in components
   */
  const handleError = useCallback((error) => {
    // Log to console in development
    if (import.meta.env.MODE === 'development') {
      console.error('Global Error:', error);
    }

    // Could integrate with error tracking service here
    // e.g., Sentry, LogRocket, etc.
    // logErrorToService(error);

    setError(error);
  }, []);

  /**
   * Clear error state
   */
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  /**
   * Log error without setting global error state
   * Useful for non-critical errors that should be tracked but not displayed
   */
  const logError = useCallback((error, context = {}) => {
    if (import.meta.env.MODE === 'development') {
      console.warn('Logged Error:', error, context);
    }
    // Send to error tracking service
    // logErrorToService(error, context);
  }, []);

  return (
    <ErrorContext.Provider
      value={{ sosError, handleError, clearError, logError }}
    >
      {children}
    </ErrorContext.Provider>
  );
};

ErrorProvider.propTypes = {
  children: PropTypes.node.isRequired,
};
